import { TranslatePipe } from '@myre/ui-primitive-core';

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  Output,
  Renderer2,
  ViewChild,
  type AfterViewChecked,
  type OnInit,
} from '@angular/core';
import {
  FormsModule,
  NG_VALUE_ACCESSOR,
  type ControlValueAccessor,
} from '@angular/forms';
import { min } from 'lodash-es';

export const TEXTAREA_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TextareaComponent),
  multi: true,
};

@Component({
  selector: 'myre-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  providers: [TEXTAREA_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe],
})
export class TextareaComponent
  implements ControlValueAccessor, OnInit, AfterViewChecked
{
  private readonly _renderer = inject(Renderer2);

  @Input()
  public error: boolean = false;

  @Input()
  public warning: boolean = false;

  @ViewChild('textarea', { static: false })
  public textarea: ElementRef;

  @Input()
  public readonly: boolean;

  @Input()
  public id: string;

  @Input()
  public disabled: boolean = false;

  @Input()
  public placeholder: string | null;

  @Input()
  public rows: number | null;

  @Input()
  public cols: number | null;

  @Input()
  public maxHeight: number | null;

  @Input()
  public autoHeight: boolean;

  @Output('onFocus')
  public onFocusEmitter = new EventEmitter<void>();

  @Output('onBlur')
  public onBlurEmitter = new EventEmitter<void>();

  public value: string;

  public ngAfterViewChecked(): void {
    this._resize();
  }

  @HostListener('keyup.enter.out-zone', ['$event'])
  public onEnter(event: Event): void {
    event.stopPropagation();
  }

  public ngOnInit(): void {
    if (!this.rows) {
      this.rows = 3;
    }
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: string) => any): void {
    this._onModelChange = fn;
  }

  public registerOnTouched(fn: () => any): void {
    this._onTouched = fn;
  }

  public setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  protected _onChange(value: string): void {
    this._onModelChange(value);
    this.writeValue(value);
  }

  protected _onFocus(): void {
    this.onFocusEmitter.emit();
  }

  protected _onBlur(): void {
    this.onBlurEmitter.emit();
    this._onTouched();
  }

  private _onModelChange: (value: string) => any = () => {};

  private _onTouched: () => any = () => {};

  private _resize(): void {
    this._renderer.setStyle(this.textarea.nativeElement, 'padding', '8px');

    if (this.autoHeight) {
      this._renderer.setStyle(this.textarea.nativeElement, 'height', '0px');

      const height = this.maxHeight
        ? min([this.maxHeight, this.textarea.nativeElement.scrollHeight])
        : this.textarea.nativeElement.scrollHeight;

      this._renderer.setStyle(
        this.textarea.nativeElement,
        'height',
        height + 'px',
      );
    }
  }
}
